import { useCallback, useEffect, useState } from 'react';
import type { EntityType } from '@erp-attach/shared';
import type { SearchResult } from '../api/types';

/** Which silo the user is searching. Customers and jobs are one world, suppliers another. */
export type SearchScope = 'all' | EntityType;

export const SEARCH_SCOPE_KEY = 'erp-attach.search-scope';
const EVENT = 'erp-attach:search-scope';

export const SCOPES: { id: SearchScope; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'customer', label: 'Customers' },
  { id: 'site', label: 'Jobs' },
  { id: 'supplier', label: 'Suppliers' },
];

function isScope(v: unknown): v is SearchScope {
  return SCOPES.some((s) => s.id === v);
}

export function readSearchScope(): SearchScope {
  try {
    const v = localStorage.getItem(SEARCH_SCOPE_KEY);
    return isScope(v) ? v : 'all';
  } catch {
    return 'all';
  }
}

export function writeSearchScope(scope: SearchScope): void {
  try {
    localStorage.setItem(SEARCH_SCOPE_KEY, scope);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event(EVENT));
  } catch {
    /* ignore */
  }
}

/** Shared, persisted scope: the hero box, the top bar and the destination picker all follow it. */
export function useSearchScope(): [SearchScope, (next: SearchScope) => void] {
  const [scope, setScope] = useState<SearchScope>(() => readSearchScope());
  useEffect(() => {
    const sync = () => setScope(readSearchScope());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const set = useCallback((next: SearchScope) => {
    setScope(next);
    writeSearchScope(next);
  }, []);
  return [scope, set];
}

/**
 * Applies the scope to a result list. Documents only appear in "All": the API does not say which
 * kind of record a document hit belongs to, so a scoped search is records only.
 */
export function filterByScope<T extends Pick<SearchResult, 'kind'>>(results: readonly T[], scope: SearchScope): T[] {
  if (scope === 'all') return [...results];
  return results.filter((r) => r.kind === scope);
}
